{/* Copyright 2026 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. */}

import {Layout} from '../../../src/Layout';
export default Layout;
import {FunctionAPIGroup} from '../../../src/FunctionAPI';
import {InterfaceType} from '../../../src/types';
import docs from 'docs:@react-aria/datepicker';
import statelyDocs from 'docs:@react-stately/datepicker';
import Anatomy from '/packages/dev/s2-docs/pages/react-aria/DatePickerAnatomy.svg';

export const section = 'Hooks';
export const description = 'Provides the behavior and accessibility implementation for a date picker component.';
export const isSubpage = true;

# useDatePicker

<PageDescription>{docs.exports.useDatePicker.description}</PageDescription>

```tsx render files={["starters/hooks/src/DatePicker.tsx", "starters/hooks/src/DatePicker.css", "starters/hooks/src/DateField.css", "starters/hooks/src/Calendar.css", "starters/hooks/src/Button.css", "starters/hooks/src/Form.css", "starters/hooks/src/Popover.css"]}
"use client";
import {DatePicker} from 'hooks-starter/DatePicker';

<DatePicker label="Event date" />
```

## API

<Anatomy />

```tsx links={{DateInput: '../DateField', DateSegment: '../DateField', Button: '../Button', Popover: '../Popover', Calendar: '../Calendar'}}
<DatePicker>
  <Label />
  <Group>
    <DateInput>
      {segment => <DateSegment segment={segment} />}
    </DateInput>
    <Button />
  </Group>
  <Popover>
    <Calendar />
  </Popover>
</DatePicker>
```

<FunctionAPIGroup
  functions={[
    {function: statelyDocs.exports.useDatePickerState, links: statelyDocs.links},
    {function: docs.exports.useDatePicker, links: docs.links},
  ]}
/>

### DatePickerState

<InterfaceType {...statelyDocs.exports.DatePickerState} links={statelyDocs.links} />

### AriaDatePickerProps

<InterfaceType {...docs.exports.AriaDatePickerProps} />

### DatePickerAria

<InterfaceType {...docs.exports.DatePickerAria} />
